// Vendor Imports
import each from 'lodash/each';
import get from 'lodash/get';
import has from 'lodash/has';
import isNil from 'lodash/isNil';
import { type ThunkDispatch } from 'redux-thunk';

// Project Imports
import * as actions from '../actions';
import { type Action } from '../actions';
import { type AuthoringWorkflowState } from '../reducers/types';

// Constants
import { CALENDAR, SERIES_TYPE_FLYOUT, SCATTER_CHART, VIF_CONSTANTS } from '../constants';
import { formatString } from 'common/js_utils/formatString';
import { getTodayDate } from 'common/dates';
import { type Vif } from 'common/visualizations/vif';

export type Dispatch = ThunkDispatch<AuthoringWorkflowState, void, Action>;

/**
 * Note: These properties are in alphabetical order.
 */
const paths = {
  actionsPrimaryKeyColumn: 'configuration.actionsPrimaryKeyColumn',
  actionsMediaType: 'configuration.actionsMediaType',
  contextFieldMappings: 'configuration.contextFieldMappings',
  annotations: 'series[0].annotations',
  additionalFlyoutColumns: 'series[{0}].mapOptions.additionalFlyoutColumns',
  baseLayerOpacity: 'configuration.baseLayerOpacity',
  baseLayerUrl: 'configuration.baseLayerUrl',
  basemapStyle: 'configuration.basemapOptions.basemapStyle',
  basemapOpacity: 'configuration.basemapOptions.basemapOpacity',
  castNullAsFalseInSeries: 'series[{0}].mapOptions.castNullAsFalseInSeries',
  charmName: 'series[{0}].mapOptions.charmName',
  clusterRadius: 'configuration.basemapOptions.clusterRadius',
  colorBoundariesBy: 'series[{0}].mapOptions.colorBoundariesBy',
  colorByBucketsCount: 'series[{0}].mapOptions.colorByBucketsCount',
  colorByColumn: 'series[2].dataSource.measure.columnName',
  colorByQuantificationMethod: 'series[{0}].mapOptions.colorByQuantificationMethod',
  colorLinesBy: 'series[{0}].mapOptions.colorLinesBy',
  colorPalette: 'series[{0}].color.palette',
  colorPointsBy: 'series[{0}].mapOptions.colorPointsBy',
  columnOrderConfiguration: 'configuration.order',
  computedColumnName: 'series[{0}].computedColumnName',
  customColorPalette: 'series[{0}].color.customPalette',
  currentDrilldownColumnName: 'series[{0}].dataSource.dimension.currentDrilldownColumnName',
  datasetName: 'series[{0}].dataSource.name',
  datasetUid: 'series[{0}].dataSource.datasetUid',
  dateDisplayFormat: 'series[0].dataSource.dateDisplayFormat',
  description: 'description',
  defaultDisplayDate: 'configuration.defaultDisplayDate',
  dimensionColumnName: 'series[{0}].dataSource.dimension.columnName',
  drilldowns: 'series[{0}].dataSource.dimension.drilldowns',
  dimensionGroupingColumnName: 'series[{0}].dataSource.dimension.grouping.columnName',
  dimensionLabelAreaSize: 'configuration.dimensionLabelAreaSize',
  dimensionLabelRotationAngle: 'configuration.dimensionLabelRotationAngle',
  domain: 'series[{0}].dataSource.domain',
  errorBarsBarColor: 'series[0].errorBars.barColor',
  errorBarsLowerBoundColumnName: 'series[0].errorBars.lowerBoundColumnName',
  errorBarsUpperBoundColumnName: 'series[0].errorBars.upperBoundColumnName',
  eventBackgroundColor: 'series[{0}].color.eventBackgroundColor',
  eventOutlineColor: 'series[{0}].color.eventOutlineColor',
  eventTextColor: 'series[{0}].color.eventTextColor',
  geoCoderControl: 'configuration.basemapOptions.geoCoderControl',
  geoLocateControl: 'configuration.basemapOptions.geoLocateControl',
  groupingOrderBy: 'series[0].dataSource.dimension.grouping.orderBy',
  hideDuplicateGroups: 'configuration.hideDuplicateGroups',
  isIndented: 'configuration.isIndented',
  rowStripeStyle: 'configuration.rowStripeStyle',
  alternateRowStripeStyle: 'configuration.alternateRowStripeStyle',
  labelBottom: 'configuration.axisLabels.bottom',
  labelLeft: 'configuration.axisLabels.left',
  labelRight: 'configuration.axisLabels.right',
  labelTop: 'configuration.axisLabels.top',
  layerToggleControl: 'configuration.basemapOptions.layerToggleControl',
  legendPosition: 'configuration.legendPosition',
  limit: 'series[0].dataSource.limit',
  lineColorOpacity: 'series[{0}].mapOptions.lineColorOpacity',
  lineWeight: 'series[{0}].mapOptions.lineWeight',
  lockCalendarViewControl: 'configuration.lockCalendarViewControl',
  logarithmicScale: 'configuration.logarithmicScale',
  mapCenterAndZoom: 'configuration.mapCenterAndZoom',
  mapFlyoutPrecision: 'configuration.mapFlyoutPrecision',
  mapFlyoutTitleColumnName: 'series[{0}].mapOptions.mapFlyoutTitleColumnName',
  mapLayerVisible: 'series[{0}].visible',
  mapLegendPrecision: 'configuration.mapLegendPrecision',
  mapPitchAndBearing: 'configuration.mapPitchAndBearing',
  mapType: 'series[{0}].mapOptions.mapType',
  mapZoomLevel: 'configuration.mapZoomLevel',
  maxClusteringZoomLevel: 'configuration.basemapOptions.maxClusteringZoomLevel',
  maxClusterSize: 'configuration.basemapOptions.maxClusterSize',
  maximumLineWeight: 'series[{0}].mapOptions.maximumLineWeight',
  maximumPointSize: 'series[{0}].mapOptions.maximumPointSize',
  maximumPlotSize: `series[${SCATTER_CHART.RESIZE_BY_SERIES_INDEX}].chartOptions.maximumPlotSize`,
  measureAggregationFunction: 'series[{0}].dataSource.measure.aggregationFunction',
  measureAxisMaxValue: 'configuration.measureAxisMaxValue',
  measureAxisMinValue: 'configuration.measureAxisMinValue',
  measureAxisPrecision: 'configuration.measureAxisPrecision',
  measureAxisScale: 'configuration.measureAxisScale',
  measureColumnName: 'series[{0}].dataSource.measure.columnName',
  midpoint: 'series[{0}].mapOptions.midpoint',
  minimumLineWeight: 'series[{0}].mapOptions.minimumLineWeight',
  minimumPointSize: 'series[{0}].mapOptions.minimumPointSize',
  minimumPlotSize: `series[${SCATTER_CHART.RESIZE_BY_SERIES_INDEX}].chartOptions.minimumPlotSize`,
  navigationControl: 'configuration.basemapOptions.navigationControl',
  negativeColor: 'configuration.legend.negativeColor',
  newTableActions: 'configuration.newTableActions',
  numberOfDataClasses: 'series[{0}].mapOptions.numberOfDataClasses',
  orderBy: 'series[0].dataSource.orderBy',
  plotSize: 'configuration.plotSize',
  pointOpacity: 'series[{0}].mapOptions.pointOpacity',
  pointAggregation: 'series[{0}].mapOptions.pointAggregation',
  pointSize: 'series[{0}].mapOptions.pointSize',
  pointThreshold: 'configuration.basemapOptions.pointThreshold',
  positiveColor: 'configuration.legend.positiveColor',
  precision: 'series[0].dataSource.precision',
  primaryColor: 'series[{0}].color.primary',
  primaryLayer: 'series[{0}].primary',
  rangeBucketType: 'series[{0}].mapOptions.rangeBucketType',
  referenceLineColor: 'referenceLines[{0}].color',
  referenceLineLabel: 'referenceLines[{0}].label',
  referenceLines: 'referenceLines',
  referenceLineValue: 'referenceLines[{0}].value',
  regionMapFlyoutColumnAndAggregations: 'series[{0}].mapOptions.regionMapFlyoutColumnAndAggregations',
  resizeByColumn: 'series[3].dataSource.measure.columnName',
  resizePointsBy: 'series[{0}].mapOptions.resizePointsBy',
  rowInspectorTitleColumnName: 'configuration.rowInspectorTitleColumnName',
  scatterChartNumberOfDataClasses: 'series[0].chartOptions.numberOfDataClasses',
  secondaryColor: 'series[{0}].color.secondary',
  secondaryMeasureAxisMaxValue: 'configuration.secondaryMeasureAxisMaxValue',
  secondaryMeasureAxisMinValue: 'configuration.secondaryMeasureAxisMinValue',
  secondaryMeasureAxisPrecision: 'configuration.secondaryMeasureAxisPrecision',
  secondaryMeasureAxisScale: 'configuration.secondaryMeasureAxisScale',
  searchBoundaryLowerRightLatitude: 'configuration.basemapOptions.searchBoundaryLowerRightLatitude',
  searchBoundaryLowerRightLongitude: 'configuration.basemapOptions.searchBoundaryLowerRightLongitude',
  searchBoundaryUpperLeftLatitude: 'configuration.basemapOptions.searchBoundaryUpperLeftLatitude',
  searchBoundaryUpperLeftLongitude: 'configuration.basemapOptions.searchBoundaryUpperLeftLongitude',
  series: 'series',
  seriesType: 'series[{0}].type',
  selectedActionForExistingColumn: 'configuration.selectedActionForExistingColumn',
  selectedExistingColumn: 'configuration.selectedExistingColumn',
  shapefileGeometryLabel: 'series[{0}].shapefile.geometryLabel',
  shapefilePrimaryKey: 'series[{0}].shapefile.primaryKey',
  shapefileUid: 'series[{0}].shapefile.uid',
  shapeFillColor: 'series[{0}].mapOptions.shapeFillColor',
  shapeFillOpacity: 'series[{0}].mapOptions.shapeFillOpacity',
  shapeOutlineColor: 'series[{0}].mapOptions.shapeOutlineColor',
  shapeOutlineWidth: 'series[{0}].mapOptions.shapeOutlineWidth',
  showAnnotationsInLegend: 'configuration.showAnnotationsInLegend',
  showDataTableControl: 'configuration.showDataTableControl',
  showDimensionLabels: 'configuration.showDimensionLabels',
  showExistingActionColumn: 'configuration.showExistingActionColumn',
  showLineStylePoints: 'series[{0}].lineStyle.points',
  showLineStylePointRadius: 'series[{0}].lineStyle.pointRadius',
  showLineValueLabels: 'configuration.showLineValueLabels',
  showNullsAsFalse: 'configuration.showNullsAsFalse',
  showOtherCategory: 'configuration.showOtherCategory',
  showSummaryTable: 'configuration.showSummaryTable',
  showValueLabels: 'configuration.showValueLabels',
  showValueLabelsAsPercent: 'configuration.showValueLabelsAsPercent',
  showLegend: 'series[{0}].showLegend',
  showLegendForMap: 'configuration.showLegendForMap',
  showLegendOpened: 'configuration.showLegendOpened',
  showMultiplePointsSymbolInLegend: 'configuration.showMultiplePointsSymbolInLegend',
  showTableActions: 'configuration.showProcessDiscoveryAction',
  simplificationLevel: 'series[{0}].mapOptions.simplificationLevel',
  stacked: 'series[0].stacked',
  stackRadius: 'configuration.basemapOptions.stackRadius',
  stackedOneHundredPercent: 'series[0].stacked.oneHundredPercent',
  tableColumns: 'series[{0}].dataSource.dimension.columns',
  tableHierarchies: 'series[{0}].dataSource.hierarchies',
  columnFormats: 'series[{0}].formatting.columnFormat',
  headerFormat: 'series[{0}].formatting.headerFormat',
  headerTextFormat: 'series[{0}].formatting.headerFormat.fontStyle',
  activeHierarchyId: 'series[{0}].dataSource.activeHierarchyId',
  title: 'title',
  treatNullValuesAsZero: 'configuration.treatNullValuesAsZero',
  unitOne: 'series[{0}].unit.one',
  unitOther: 'series[{0}].unit.other',
  useSecondaryAxisForColumns: 'configuration.useSecondaryAxisForColumns',
  useSecondaryAxisForLines: 'configuration.useSecondaryAxisForLines',
  viewSourceDataLink: 'configuration.viewSourceDataLink',
  weighLinesBy: 'series[{0}].mapOptions.weighLinesBy',
  wrapDimensionLabels: 'configuration.wrapDimensionLabels',
  xAxisScalingMode: 'configuration.xAxisScalingMode',
  xAxisColumn: 'series[0].dataSource.measure.columnName',
  yAxisColumn: 'series[1].dataSource.measure.columnName',
  zeroColor: 'configuration.legend.zeroColor',
};

const hasVifPath = (vif: Partial<Vif>) => (path: string) => has(vif, path);
const getVifPath = (vif: Partial<Vif>) => (path: string) => get(vif, path, null);

/**
 * load() communicates to the redux store the individual
 * pieces of any VIF it encounters and attempts to create
 * actions. These actions are then processed by each VIF reducer and
 * applied to its configuration (if relevant).
 *
 * Loading also includes kicking off the dataSource HTTP requests
 * that grab dataset metadata.
 */
export const load = (dispatch: Dispatch, vif: Partial<Vif>) => {
  const hasPath = hasVifPath(vif);
  const getPath = getVifPath(vif);
  let primarySeriesIndex = 0;

  if (hasPath(paths.series)) {
    const seriesCount = getPath(paths.series).length;
    let flyoutSeriesIndex = 0;

    for (let seriesIndex = 0; seriesIndex < seriesCount; seriesIndex++) {
      const seriesTypePath = formatString(paths.seriesType, seriesIndex);

      // Series types can be of the form: '{type}' or '{type}.{variant}'.
      // For instance: 'columnChart' or 'comboChart.line'
      // The visualizationType uses the first segment of the type of the first series.
      let seriesType;
      let seriesVariant;
      let visualizationType;

      if (hasPath(seriesTypePath)) {
        seriesType = getPath(seriesTypePath);
        visualizationType = seriesType;

        const parts = seriesType.split('.');

        if (parts.length > 1) {
          visualizationType = parts[0];
          seriesVariant = parts[1];
        }
      }

      const isFlyoutSeries = seriesType === SERIES_TYPE_FLYOUT;
      const isMapSeries = seriesType === 'map';

      // Each visType has a default vif and they have default series array. For scatterChart the default
      // vif has four default empty series items whereas other charts have empty series array. So based
      // on the defaultSeriesLength, we append seriesItem to series or use the existing ones.
      let defaultSeriesLength;
      if (seriesType === 'scatterChart') {
        defaultSeriesLength = SCATTER_CHART.DEFAULT_VIF_SERIES_LENGTH;
      } else if (seriesType === 'calendar') {
        defaultSeriesLength = CALENDAR.DEFAULT_VIF_SERIES_LENGTH;
      } else {
        defaultSeriesLength = VIF_CONSTANTS.DEFAULT_VIF_SERIES_LENGTH;
      }

      if (seriesIndex > defaultSeriesLength) {
        // the first series already exists in the vif templates, no need to create it.

        dispatch(
          actions.appendSeries({
            isFlyoutSeries,
            isInitialLoad: true,
            seriesVariant,
            isMapSeries,
          }),
        );
      } else {
        // Set visualization type to the type of the first series only
        if (!isNil(visualizationType)) {
          dispatch(actions.setVisualizationType(visualizationType));
        }

        // At present this only affects combo chart whose series type at index 0 could be,
        // comboChart.column or comboChart.line.  It defaults to comboChart.column so we need to
        // explicitly set the variant it to the vif value.
        if (!isNil(seriesVariant)) {
          dispatch(actions.setSeriesVariant(seriesIndex, seriesVariant));
        }
      }

      if (isMapSeries) {
        dispatch(actions.setCurrentMapLayerIndex(seriesIndex));

        if (getPath(formatString(paths.primaryLayer, seriesIndex))) {
          primarySeriesIndex = seriesIndex;
        }
      }

      const measureColumnNamePath = formatString(paths.measureColumnName, seriesIndex);
      if (hasPath(measureColumnNamePath)) {
        dispatch(
          actions.setMeasureColumn({
            columnName: getPath(measureColumnNamePath),
            isFlyoutSeries,
            relativeIndex: isFlyoutSeries ? flyoutSeriesIndex : seriesIndex,
          }),
        );
      }

      const measureAggregationFunctionPath = formatString(paths.measureAggregationFunction, seriesIndex);
      if (hasPath(measureAggregationFunctionPath)) {
        dispatch(
          actions.setMeasureAggregation({
            aggregationFunction: getPath(measureAggregationFunctionPath),
            isFlyoutSeries,
            relativeIndex: isFlyoutSeries ? flyoutSeriesIndex : seriesIndex,
          }),
        );
      }

      const primaryColorPath = formatString(paths.primaryColor, seriesIndex);
      if (hasPath(primaryColorPath)) {
        dispatch(actions.setPrimaryColor(seriesIndex, getPath(primaryColorPath)));
      }

      const primaryLayerPath = formatString(paths.primaryLayer, seriesIndex);
      if (hasPath(primaryLayerPath)) {
        dispatch(actions.setLayerAsPrimary(seriesIndex, getPath(primaryLayerPath)));
      }

      const secondaryColorPath = formatString(paths.secondaryColor, seriesIndex);
      if (hasPath(secondaryColorPath)) {
        dispatch(actions.setSecondaryColor(seriesIndex, getPath(secondaryColorPath)));
      }

      const unitOnePath = formatString(paths.unitOne, seriesIndex);
      if (hasPath(unitOnePath)) {
        dispatch(actions.setUnitsOne(seriesIndex, getPath(unitOnePath)));
      }

      const unitOtherPath = formatString(paths.unitOther, seriesIndex);
      if (hasPath(unitOtherPath)) {
        dispatch(actions.setUnitsOther(seriesIndex, getPath(unitOtherPath)));
      }

      const pointOpacityPath = formatString(paths.pointOpacity, seriesIndex);
      if (hasPath(pointOpacityPath)) {
        dispatch(actions.setPointOpacity(getPath(pointOpacityPath)));
      }

      const pointSizePath = formatString(paths.pointSize, seriesIndex);
      if (hasPath(pointSizePath)) {
        dispatch(actions.setPointSize(getPath(pointSizePath)));
      }

      const lineWeightPath = formatString(paths.lineWeight, seriesIndex);
      if (hasPath(lineWeightPath)) {
        dispatch(actions.setLineWeight(getPath(lineWeightPath)));
      }

      const lineColorOpacityPath = formatString(paths.lineColorOpacity, seriesIndex);
      if (hasPath(lineColorOpacityPath)) {
        dispatch(actions.setLineColorOpacity(getPath(lineColorOpacityPath)));
      }

      const lineStylePoints = formatString(paths.showLineStylePoints, seriesIndex);
      if (hasPath(lineStylePoints)) {
        dispatch(actions.setLineStylePoints(getPath(lineStylePoints)));
      }

      const lineStylePointRadius = formatString(paths.showLineStylePointRadius, seriesIndex);
      if (hasPath(lineStylePointRadius)) {
        dispatch(actions.setLineStylePointRadius(getPath(lineStylePointRadius)));
      }

      const minimumLineWeightPath = formatString(paths.minimumLineWeight, seriesIndex);
      if (hasPath(minimumLineWeightPath)) {
        dispatch(actions.setMinimumLineWeight(getPath(minimumLineWeightPath)));
      }

      const maximumLineWeightPath = formatString(paths.maximumLineWeight, seriesIndex);
      if (hasPath(maximumLineWeightPath)) {
        dispatch(actions.setMaximumLineWeight(getPath(maximumLineWeightPath)));
      }

      const minimumPointSizePath = formatString(paths.minimumPointSize, seriesIndex);
      if (hasPath(minimumPointSizePath)) {
        dispatch(actions.setMinimumPointSize(getPath(minimumPointSizePath)));
      }

      const maximumPointSizePath = formatString(paths.maximumPointSize, seriesIndex);
      if (hasPath(maximumPointSizePath)) {
        dispatch(actions.setMaximumPointSize(getPath(maximumPointSizePath)));
      }

      const numberOfDataClassesPath = formatString(paths.numberOfDataClasses, seriesIndex);
      if (hasPath(numberOfDataClassesPath)) {
        dispatch(actions.setNumberOfDataClasses(getPath(numberOfDataClassesPath)));
      }

      const colorByBucketsCountPath = formatString(paths.colorByBucketsCount, seriesIndex);
      if (hasPath(colorByBucketsCountPath)) {
        dispatch(actions.setColorByBucketsCount(getPath(colorByBucketsCountPath)));
      }

      const midpointPath = formatString(paths.midpoint, seriesIndex);
      if (hasPath(midpointPath)) {
        dispatch(actions.setMidpoint(getPath(midpointPath)));
      }

      const resizePointsByPath = formatString(paths.resizePointsBy, seriesIndex);
      if (hasPath(resizePointsByPath)) {
        dispatch(actions.setResizePointsByColumn(getPath(resizePointsByPath)));
      }

      const colorPointsByPath = formatString(paths.colorPointsBy, seriesIndex);
      if (hasPath(colorPointsByPath)) {
        dispatch(actions.setColorPointsByColumn(getPath(colorPointsByPath)));
      }

      const eventBackgroundColorPath = formatString(paths.eventBackgroundColor, seriesIndex);
      if (hasPath(eventBackgroundColorPath)) {
        dispatch(actions.setEventBackgroundColor(getPath(eventBackgroundColorPath)));
      }

      const eventOutlineColorPath = formatString(paths.eventOutlineColor, seriesIndex);
      if (hasPath(eventOutlineColorPath)) {
        dispatch(actions.setEventOutlineColor(getPath(eventOutlineColorPath)));
      }

      const eventTextColorPath = formatString(paths.eventTextColor, seriesIndex);
      if (hasPath(eventTextColorPath)) {
        dispatch(actions.setEventTextColor(getPath(eventTextColorPath)));
      }

      const weighLinesByPath = formatString(paths.weighLinesBy, seriesIndex);
      if (hasPath(weighLinesByPath)) {
        dispatch(actions.setWeighLinesByColumn(getPath(weighLinesByPath)));
      }

      const colorLinesByPath = formatString(paths.colorLinesBy, seriesIndex);
      if (hasPath(colorLinesByPath)) {
        dispatch(actions.setColorLinesByColumn(getPath(colorLinesByPath)));
      }

      const colorBoundariesByPath = formatString(paths.colorBoundariesBy, seriesIndex);
      if (hasPath(colorBoundariesByPath)) {
        dispatch(actions.setBoundaryColorByColumn(getPath(colorBoundariesByPath)));
      }

      const mapTypePath = formatString(paths.mapType, seriesIndex);
      if (hasPath(mapTypePath)) {
        dispatch(actions.setMapType(getPath(mapTypePath)));
      }

      const colorByQuantificationMethodPath = formatString(paths.colorByQuantificationMethod, seriesIndex);
      if (hasPath(colorByQuantificationMethodPath)) {
        dispatch(actions.setColorByQuantificationMethod(getPath(colorByQuantificationMethodPath)));
      }

      const rangeBucketTypePath = formatString(paths.rangeBucketType, seriesIndex);
      if (hasPath(rangeBucketTypePath)) {
        dispatch(actions.setRangeBucketType(getPath(rangeBucketTypePath)));
      }

      const pointAggregationPath = formatString(paths.pointAggregation, seriesIndex);
      if (hasPath(pointAggregationPath)) {
        dispatch(actions.setPointAggregation(getPath(pointAggregationPath)));
      }

      const shapefileGeometryLabelPath = formatString(paths.shapefileGeometryLabel, seriesIndex);
      if (hasPath(shapefileGeometryLabelPath)) {
        dispatch(actions.setShapefileGeometryLabel(getPath(shapefileGeometryLabelPath)));
      }

      const shapefilePrimaryKeyPath = formatString(paths.shapefilePrimaryKey, seriesIndex);
      if (hasPath(shapefilePrimaryKeyPath)) {
        dispatch(actions.setShapefilePrimaryKey(getPath(shapefilePrimaryKeyPath)));
      }

      const shapefileUidPath = formatString(paths.shapefileUid, seriesIndex);
      if (hasPath(shapefileUidPath)) {
        dispatch(actions.setShapefileUid(getPath(shapefileUidPath)));
      }

      const mapFlyoutTitleColumnNamePath = formatString(paths.mapFlyoutTitleColumnName, seriesIndex);
      if (hasPath(mapFlyoutTitleColumnNamePath)) {
        dispatch(actions.setMapFlyoutTitleColumnName(getPath(mapFlyoutTitleColumnNamePath)));
      }

      const additionalFlyoutColumnsPath = formatString(paths.additionalFlyoutColumns, seriesIndex);
      if (hasPath(additionalFlyoutColumnsPath)) {
        dispatch(actions.setAdditionalFlyoutColumns(getPath(additionalFlyoutColumnsPath)));
      }

      const regionMapFlyoutColumnAndAggregationsPath = formatString(
        paths.regionMapFlyoutColumnAndAggregations,
        seriesIndex,
      );
      if (hasPath(regionMapFlyoutColumnAndAggregationsPath)) {
        dispatch(
          actions.setRegionMapFlyoutColumnAndAggregations(getPath(regionMapFlyoutColumnAndAggregationsPath)),
        );
      }

      const shapeFillColorPath = formatString(paths.shapeFillColor, seriesIndex);
      if (hasPath(shapeFillColorPath)) {
        dispatch(actions.setShapeFillColor(getPath(shapeFillColorPath)));
      }

      const shapeFillOpacityPath = formatString(paths.shapeFillOpacity, seriesIndex);
      if (hasPath(shapeFillOpacityPath)) {
        dispatch(actions.setShapeFillOpacity(getPath(shapeFillOpacityPath)));
      }

      const shapeOutlineColorPath = formatString(paths.shapeOutlineColor, seriesIndex);
      if (hasPath(shapeOutlineColorPath)) {
        dispatch(actions.setShapeOutlineColor(getPath(shapeOutlineColorPath)));
      }

      const shapeOutlineWidthPath = formatString(paths.shapeOutlineWidth, seriesIndex);
      if (hasPath(shapeOutlineWidthPath)) {
        dispatch(actions.setShapeOutlineWidth(getPath(shapeOutlineWidthPath)));
      }

      const mapLayerVisiblePath = formatString(paths.mapLayerVisible, seriesIndex);
      if (hasPath(mapLayerVisiblePath)) {
        dispatch(actions.setMapLayerVisible(seriesIndex, getPath(mapLayerVisiblePath)));
      }

      const simplificationLevelPath = formatString(paths.simplificationLevel, seriesIndex);
      if (hasPath(simplificationLevelPath)) {
        dispatch(actions.setSimplificationLevel(seriesIndex, getPath(simplificationLevelPath)));
      }

      const castNullAsFalseInSeriesPath = formatString(paths.castNullAsFalseInSeries, seriesIndex);
      if (hasPath(castNullAsFalseInSeriesPath)) {
        dispatch(actions.setCastNullAsFalseInSeries(getPath(castNullAsFalseInSeriesPath)));
      }

      const charmNamePath = formatString(paths.charmName, seriesIndex);
      if (hasPath(charmNamePath)) {
        dispatch(actions.setCharmName(seriesIndex, getPath(charmNamePath)));
      }

      const showLegendPath = formatString(paths.showLegend, seriesIndex);
      if (hasPath(showLegendPath)) {
        dispatch(actions.setShowLegend(getPath(showLegendPath)));
      }

      if (hasPath(paths.showLegendForMap)) {
        dispatch(actions.setShowLegendForMap(getPath(paths.showLegendForMap)));
      }

      const computedColumnNamePath = formatString(paths.computedColumnName, seriesIndex);
      if (hasPath(computedColumnNamePath)) {
        dispatch(actions.setComputedColumn(getPath(computedColumnNamePath)));
      }

      const customColorPalettePath = formatString(paths.customColorPalette, seriesIndex);
      if (hasPath(customColorPalettePath)) {
        each(getPath(customColorPalettePath), (palette, columnName) => {
          dispatch(actions.setCustomColorPalette(palette, columnName, seriesIndex));
        });
      }

      const datasetNamePath = formatString(paths.datasetName, seriesIndex);
      if (hasPath(datasetNamePath)) {
        dispatch(actions.setMapLayerName(getPath(datasetNamePath)));
      }

      const datasetUidPath = formatString(paths.datasetUid, seriesIndex);
      if (hasPath(datasetUidPath)) {
        dispatch(actions.setDatasetUid(getPath(datasetUidPath)));
      }

      const domainPath = formatString(paths.domain, seriesIndex);
      if (hasPath(domainPath)) {
        dispatch(actions.setDomain(getPath(domainPath)));
      }

      const dimensionColumnNamePath = formatString(paths.dimensionColumnName, seriesIndex);
      if (hasPath(dimensionColumnNamePath)) {
        dispatch(actions.setDimension(getPath(dimensionColumnNamePath)));
      }

      const currentDrilldownColumnNamePath = formatString(paths.currentDrilldownColumnName, seriesIndex);
      if (hasPath(currentDrilldownColumnNamePath)) {
        dispatch(actions.setCurrentDrilldownColumnName(getPath(currentDrilldownColumnNamePath)));
      }

      const drilldownsPath = formatString(paths.drilldowns, seriesIndex);
      if (hasPath(drilldownsPath)) {
        dispatch(actions.setDrilldowns(getPath(drilldownsPath)));
      }

      const tableColumnsPath = formatString(paths.tableColumns, seriesIndex);
      if (hasPath(tableColumnsPath)) {
        dispatch(actions.setTableColumns(getPath(tableColumnsPath)));
      }

      const tableHierarchiesPath = formatString(paths.tableHierarchies, seriesIndex);
      if (hasPath(tableHierarchiesPath)) {
        dispatch(actions.setTableHierarchies(getPath(tableHierarchiesPath)));
      }

      const columnFormatsPath = formatString(paths.columnFormats, seriesIndex);
      if (hasPath(columnFormatsPath)) {
        dispatch(actions.setTableVisualizationColumnFormats(getPath(columnFormatsPath)));
      }

      const headerFormatPath = formatString(paths.headerFormat, seriesIndex);
      if (hasPath(headerFormatPath)) {
        dispatch(actions.updateTableVisualizationHeaderStyle(getPath(headerFormatPath)));
      }

      const headerTextFormatPath = formatString(paths.headerTextFormat, seriesIndex);
      if (hasPath(headerTextFormatPath)) {
        dispatch(actions.updateTableVisualizationHeaderTextStyle(getPath(headerTextFormatPath)));
      }

      const activeHierarchyIdPath = formatString(paths.activeHierarchyId, seriesIndex);
      if (hasPath(activeHierarchyIdPath)) {
        dispatch(actions.setActiveHierarchyId(getPath(activeHierarchyIdPath)));
      }

      const dimensionGroupingColumnNamePath = formatString(paths.dimensionGroupingColumnName, seriesIndex);
      if (hasPath(dimensionGroupingColumnNamePath)) {
        dispatch(actions.setDimensionGroupingColumnName(getPath(dimensionGroupingColumnNamePath)));
      }

      // This must happen after the grouping column name is set on the vif.
      const colorPalettePath = formatString(paths.colorPalette, seriesIndex);
      if (hasPath(colorPalettePath)) {
        dispatch(actions.setColorPalette(getPath(colorPalettePath)));
      }

      dispatch(
        actions.setDataSource(
          getPath(formatString(paths.domain, seriesIndex)),
          getPath(formatString(paths.datasetUid, seriesIndex)),
          null,
          true,
          seriesIndex,
        ),
      );

      dispatch(
        actions.setCuratedRegions(
          getPath(formatString(paths.domain, seriesIndex)),
          getPath(formatString(paths.datasetUid, seriesIndex)),
          seriesIndex,
        ),
      );

      dispatch(
        actions.setComputedRegionColumns(
          getPath(formatString(paths.domain, seriesIndex)),
          getPath(formatString(paths.datasetUid, seriesIndex)),
          seriesIndex,
        ),
      );

      if (isFlyoutSeries) {
        flyoutSeriesIndex++;
      }

      if (isMapSeries && seriesIndex !== VIF_CONSTANTS.DEFAULT_VIF_SERIES_LENGTH) {
        dispatch(actions.setColorPaletteProperties());
      }
    }

    if (getPath(formatString(paths.seriesType, 0)) === 'map') {
      dispatch(actions.setCurrentMapLayerIndex(primarySeriesIndex));
      dispatch(actions.setCurrentMapLayerMode('list_view'));
    }
  }

  if (hasPath(paths.referenceLines)) {
    const referenceLinesCount = getPath(paths.referenceLines).length;

    for (let i = 0; i < referenceLinesCount; i++) {
      dispatch(actions.appendReferenceLine());

      const referenceLineColorPath = formatString(paths.referenceLineColor, i);
      if (hasPath(referenceLineColorPath)) {
        dispatch(
          actions.setReferenceLineColor({ referenceLineIndex: i, color: getPath(referenceLineColorPath) }),
        );
      }

      const referenceLineLabelPath = formatString(paths.referenceLineLabel, i);
      if (hasPath(referenceLineLabelPath)) {
        dispatch(
          actions.setReferenceLineLabel({ referenceLineIndex: i, label: getPath(referenceLineLabelPath) }),
        );
      }

      const referenceLineValuePath = formatString(paths.referenceLineValue, i);
      if (hasPath(referenceLineValuePath)) {
        dispatch(
          actions.setReferenceLineValue({ referenceLineIndex: i, value: getPath(referenceLineValuePath) }),
        );
      }
    }
  }

  if (hasPath(paths.baseLayerOpacity)) {
    dispatch(actions.setBaseLayerOpacity(getPath(paths.baseLayerOpacity)));
  }

  if (hasPath(paths.baseLayerUrl)) {
    dispatch(actions.setBaseLayer(getPath(paths.baseLayerUrl)));
  }

  if (hasPath(paths.dateDisplayFormat)) {
    dispatch(actions.setDateDisplayFormat(getPath(paths.dateDisplayFormat)));
  }

  if (hasPath(paths.description)) {
    dispatch(actions.setDescription(getPath(paths.description)));
  }

  if (hasPath(paths.columnOrderConfiguration)) {
    dispatch(actions.setColumnOrderConfiguration(getPath(paths.columnOrderConfiguration)));
  }

  if (hasPath(paths.errorBarsBarColor)) {
    dispatch(actions.setErrorBarsBarColor(getPath(paths.errorBarsBarColor)));
  }

  if (hasPath(paths.errorBarsLowerBoundColumnName)) {
    dispatch(actions.setErrorBarsLowerBoundColumnName(getPath(paths.errorBarsLowerBoundColumnName)));
  }

  if (hasPath(paths.errorBarsUpperBoundColumnName)) {
    dispatch(actions.setErrorBarsUpperBoundColumnName(getPath(paths.errorBarsUpperBoundColumnName)));
  }

  if (hasPath(paths.hideDuplicateGroups)) {
    dispatch(actions.setHideDuplicateGroups(getPath(paths.hideDuplicateGroups)));
  }

  if (hasPath(paths.isIndented)) {
    dispatch(actions.setTableIndentedLayout(getPath(paths.isIndented)));
  }

  if (hasPath(paths.labelBottom)) {
    dispatch(actions.setLabelBottom(getPath(paths.labelBottom)));
  }

  if (hasPath(paths.labelLeft)) {
    dispatch(actions.setLabelLeft(getPath(paths.labelLeft)));
  }

  if (hasPath(paths.labelRight)) {
    dispatch(actions.setLabelRight(getPath(paths.labelRight)));
  }

  if (hasPath(paths.labelTop)) {
    dispatch(actions.setLabelTop(getPath(paths.labelTop)));
  }

  if (hasPath(paths.legendPosition)) {
    dispatch(actions.setLegendPosition(getPath(paths.legendPosition)));
  }

  if (hasPath(paths.lockCalendarViewControl)) {
    dispatch(actions.setLockCalendarViewControl(getPath(paths.lockCalendarViewControl)));
  }

  if (hasPath(paths.logarithmicScale)) {
    dispatch(actions.setLogarithmicScale(getPath(paths.logarithmicScale)));
  }

  if (hasPath(paths.dimensionLabelAreaSize)) {
    dispatch(actions.setDimensionLabelAreaSize(getPath(paths.dimensionLabelAreaSize)));
  }

  if (hasPath(paths.defaultDisplayDate)) {
    const lockCalendarViewControl = getPath(paths.lockCalendarViewControl);

    if (lockCalendarViewControl) {
      dispatch(actions.setDefaultDisplayDate(getPath(paths.defaultDisplayDate)));
    } else {
      dispatch(actions.setDefaultDisplayDate(getTodayDate()));
    }
  }

  if (hasPath(paths.dimensionLabelRotationAngle)) {
    dispatch(actions.setDimensionLabelRotationAngle(getPath(paths.dimensionLabelRotationAngle)));
  }

  if (hasPath(paths.limit)) {
    if (hasPath(paths.showOtherCategory)) {
      dispatch(
        actions.setLimitCountAndShowOtherCategory(getPath(paths.limit), getPath(paths.showOtherCategory)),
      );
    } else {
      dispatch(actions.setLimitCountAndShowOtherCategory(getPath(paths.limit), false));
    }
  } else if (hasPath(paths.showOtherCategory)) {
    dispatch(actions.setShowOtherCategory(getPath(paths.showOtherCategory)));
  }

  if (hasPath(paths.mapCenterAndZoom)) {
    dispatch(actions.setCenterAndZoom(getPath(paths.mapCenterAndZoom)));
  }

  if (hasPath(paths.measureAxisMaxValue)) {
    dispatch(actions.setMeasureAxisMaxValue(getPath(paths.measureAxisMaxValue)));
  }

  if (hasPath(paths.measureAxisMinValue)) {
    dispatch(actions.setMeasureAxisMinValue(getPath(paths.measureAxisMinValue)));
  }

  if (hasPath(paths.measureAxisPrecision)) {
    dispatch(actions.setMeasureAxisPrecision(getPath(paths.measureAxisPrecision)));
  }

  if (hasPath(paths.mapLegendPrecision)) {
    dispatch(actions.setMapLegendPrecision(getPath(paths.mapLegendPrecision)));
  }

  if (hasPath(paths.mapFlyoutPrecision)) {
    dispatch(actions.setMapFlyoutPrecision(getPath(paths.mapFlyoutPrecision)));
  }

  if (hasPath(paths.measureAxisScale)) {
    dispatch(actions.setMeasureAxisScale(getPath(paths.measureAxisScale)));
  }

  if (hasPath(paths.secondaryMeasureAxisMaxValue)) {
    dispatch(actions.setSecondaryMeasureAxisMaxValue(getPath(paths.secondaryMeasureAxisMaxValue)));
  }

  if (hasPath(paths.secondaryMeasureAxisMinValue)) {
    dispatch(actions.setSecondaryMeasureAxisMinValue(getPath(paths.secondaryMeasureAxisMinValue)));
  }

  if (hasPath(paths.secondaryMeasureAxisPrecision)) {
    dispatch(actions.setSecondaryMeasureAxisPrecision(getPath(paths.secondaryMeasureAxisPrecision)));
  }

  if (hasPath(paths.secondaryMeasureAxisScale)) {
    dispatch(actions.setSecondaryMeasureAxisScale(getPath(paths.secondaryMeasureAxisScale)));
  }

  if (hasPath(paths.negativeColor)) {
    dispatch(actions.setNegativeColor(getPath(paths.negativeColor)));
  }

  if (hasPath(paths.groupingOrderBy)) {
    dispatch(actions.setGroupingOrderBy(getPath(paths.groupingOrderBy)));
  }

  if (hasPath(paths.orderBy)) {
    dispatch(actions.setOrderBy(getPath(paths.orderBy)));
  }

  if (hasPath(paths.positiveColor)) {
    dispatch(actions.setPositiveColor(getPath(paths.positiveColor)));
  }

  if (hasPath(paths.precision)) {
    dispatch(actions.setPrecision(getPath(paths.precision)));
  }

  if (hasPath(paths.rowInspectorTitleColumnName)) {
    dispatch(actions.setRowInspectorTitleColumnName(getPath(paths.rowInspectorTitleColumnName)));
  }

  if (hasPath(paths.showDimensionLabels)) {
    dispatch(actions.setShowDimensionLabels(getPath(paths.showDimensionLabels)));
  }

  if (hasPath(paths.showLineValueLabels)) {
    dispatch(actions.setShowLineValueLabels(getPath(paths.showLineValueLabels)));
  }

  if (hasPath(paths.showNullsAsFalse)) {
    dispatch(actions.setShowNullsAsFalse(getPath(paths.showNullsAsFalse)));
  }

  if (hasPath(paths.showValueLabels)) {
    dispatch(actions.setShowValueLabels(getPath(paths.showValueLabels)));
  }

  if (hasPath(paths.showValueLabelsAsPercent)) {
    dispatch(actions.setShowValueLabelsAsPercent(getPath(paths.showValueLabelsAsPercent)));
  }

  if (hasPath(paths.showLegendOpened)) {
    dispatch(actions.setShowLegendOpened(getPath(paths.showLegendOpened)));
  }

  if (hasPath(paths.showMultiplePointsSymbolInLegend)) {
    dispatch(actions.setShowMultiplePointsSymbolInLegend(getPath(paths.showMultiplePointsSymbolInLegend)));
  }

  if (hasPath(paths.showTableActions)) {
    dispatch(actions.setShowTableActions(getPath(paths.showTableActions)));
  }

  if (hasPath(paths.showExistingActionColumn)) {
    dispatch(actions.setShowExistingActionColumn(getPath(paths.showExistingActionColumn)));
  }

  if (hasPath(paths.selectedExistingColumn)) {
    dispatch(actions.setSelectedExistingColumn(getPath(paths.selectedExistingColumn)));
  }

  if (hasPath(paths.newTableActions)) {
    dispatch(actions.setNewTableActions(getPath(paths.newTableActions)));
  }

  if (hasPath(paths.actionsMediaType)) {
    dispatch(actions.setTableActionsMediaType(getPath(paths.actionsMediaType)));
  }

  if (hasPath(paths.contextFieldMappings)) {
    dispatch(actions.setTableActionsContextFieldsMapping(getPath(paths.contextFieldMappings)));
  }

  if (hasPath(paths.selectedActionForExistingColumn)) {
    dispatch(actions.setSelectedActionForExistingColumn(getPath(paths.selectedActionForExistingColumn)));
  }

  if (hasPath(paths.stackedOneHundredPercent)) {
    dispatch(
      actions.setStacked({ stacked: true, oneHundredPercent: getPath(paths.stackedOneHundredPercent) }),
    );
  } else if (hasPath(paths.stacked)) {
    dispatch(actions.setStacked({ stacked: true, oneHundredPercent: false }));
  }

  if (hasPath(paths.title)) {
    dispatch(actions.setTitle(getPath(paths.title)));
  }

  if (hasPath(paths.treatNullValuesAsZero)) {
    dispatch(actions.setTreatNullValuesAsZero(getPath(paths.treatNullValuesAsZero)));
  }

  if (hasPath(paths.useSecondaryAxisForColumns)) {
    dispatch(actions.setUseSecondaryAxisForColumns(getPath(paths.useSecondaryAxisForColumns)));
  }

  if (hasPath(paths.useSecondaryAxisForLines)) {
    dispatch(actions.setUseSecondaryAxisForLines(getPath(paths.useSecondaryAxisForLines)));
  }

  if (hasPath(paths.viewSourceDataLink)) {
    dispatch(actions.setViewSourceDataLink(getPath(paths.viewSourceDataLink)));
  }

  if (hasPath(paths.annotations)) {
    dispatch(actions.setAnnotations(getPath(paths.annotations)));
  }

  if (hasPath(paths.wrapDimensionLabels)) {
    dispatch(actions.setWrapDimensionLabels(getPath(paths.wrapDimensionLabels)));
  }

  if (hasPath(paths.xAxisScalingMode)) {
    const shouldFit = getPath(paths.xAxisScalingMode) === 'fit';
    dispatch(actions.setXAxisScalingMode({ shouldFit }));
  }

  if (hasPath(paths.plotSize)) {
    dispatch(actions.setPlotSize(getPath(paths.plotSize)));
  }

  if (hasPath(paths.xAxisColumn)) {
    dispatch(actions.setXAxisColumn(getPath(paths.xAxisColumn)));
  }

  const isCalendarType =
    getPath(formatString(paths.seriesType, CALENDAR.START_DATE_SERIES_INDEX)) === 'calendar';
  if (isCalendarType) {
    const startDateColumnPath = formatString(paths.dimensionColumnName, CALENDAR.START_DATE_SERIES_INDEX);
    if (startDateColumnPath) {
      dispatch(actions.setStartDateColumn(getPath(startDateColumnPath)));
    }

    const endDateColumnPath = formatString(paths.dimensionColumnName, CALENDAR.END_DATE_SERIES_INDEX);
    if (endDateColumnPath) {
      dispatch(actions.setEndDateColumn(getPath(endDateColumnPath)));
    }

    const eventTitleColumnPath = formatString(paths.dimensionColumnName, CALENDAR.EVENT_TITLE_SERIES_INDEX);
    if (eventTitleColumnPath) {
      dispatch(actions.setEventTitleColumn(getPath(eventTitleColumnPath)));
    }
  }

  if (hasPath(paths.minimumPlotSize)) {
    dispatch(actions.setMinimumPlotSize(getPath(paths.minimumPlotSize)));
  }

  if (hasPath(paths.maximumPlotSize)) {
    dispatch(actions.setMaximumPlotSize(getPath(paths.maximumPlotSize)));
  }

  if (hasPath(paths.scatterChartNumberOfDataClasses)) {
    dispatch(actions.setNumberOfDataClasses(getPath(paths.scatterChartNumberOfDataClasses)));
  }

  if (hasPath(paths.yAxisColumn)) {
    dispatch(actions.setYAxisColumn(getPath(paths.yAxisColumn)));
  }

  if (hasPath(paths.zeroColor)) {
    dispatch(actions.setZeroColor(getPath(paths.zeroColor)));
  }

  if (hasPath(paths.maxClusteringZoomLevel)) {
    dispatch(actions.setMaxClusteringZoomLevel(getPath(paths.maxClusteringZoomLevel)));
  }

  if (hasPath(paths.pointThreshold)) {
    dispatch(actions.setPointThreshold(getPath(paths.pointThreshold)));
  }

  if (hasPath(paths.clusterRadius)) {
    dispatch(actions.setClusterRadius(getPath(paths.clusterRadius)));
  }

  if (hasPath(paths.maxClusterSize)) {
    dispatch(actions.setMaxClusterSize(getPath(paths.maxClusterSize)));
  }

  if (hasPath(paths.colorByColumn)) {
    const colorByColumnSeriesType = getPath(
      formatString(paths.seriesType, SCATTER_CHART.COLOR_BY_SERIES_INDEX),
    );

    if (colorByColumnSeriesType === 'scatterChart') {
      dispatch(actions.setColorByColumn(getPath(paths.colorByColumn)));
    }
  }

  if (hasPath(paths.resizeByColumn)) {
    const resizeByColumnSeriesType = getPath(
      formatString(paths.seriesType, SCATTER_CHART.RESIZE_BY_SERIES_INDEX),
    );

    if (resizeByColumnSeriesType === 'scatterChart') {
      dispatch(actions.setResizeByColumn(getPath(paths.resizeByColumn)));
    }
  }

  if (hasPath(paths.stackRadius)) {
    dispatch(actions.setStackRadius(getPath(paths.stackRadius)));
  }

  if (hasPath(paths.navigationControl)) {
    dispatch(actions.setNavigationControl(getPath(paths.navigationControl)));
  }

  if (hasPath(paths.layerToggleControl)) {
    dispatch(actions.setLayerToggleControl(getPath(paths.layerToggleControl)));
  }

  if (hasPath(paths.geoCoderControl)) {
    dispatch(actions.setGeoCoderControl(getPath(paths.geoCoderControl)));
  }

  if (hasPath(paths.geoLocateControl)) {
    dispatch(actions.setGeoLocateControl(getPath(paths.geoLocateControl)));
  }

  if (hasPath(paths.searchBoundaryLowerRightLatitude)) {
    dispatch(actions.setSearchBoundaryLowerRightLatitude(getPath(paths.searchBoundaryLowerRightLatitude)));
  }

  if (hasPath(paths.searchBoundaryLowerRightLongitude)) {
    dispatch(actions.setSearchBoundaryLowerRightLongitude(getPath(paths.searchBoundaryLowerRightLongitude)));
  }

  if (hasPath(paths.searchBoundaryUpperLeftLatitude)) {
    dispatch(actions.setSearchBoundaryUpperLeftLatitude(getPath(paths.searchBoundaryUpperLeftLatitude)));
  }

  if (hasPath(paths.searchBoundaryUpperLeftLongitude)) {
    dispatch(actions.setSearchBoundaryUpperLeftLongitude(getPath(paths.searchBoundaryUpperLeftLongitude)));
  }

  if (hasPath(paths.basemapStyle)) {
    dispatch(actions.setBasemapStyle(getPath(paths.basemapStyle)));
  }

  if (hasPath(paths.basemapOpacity)) {
    dispatch(actions.setBasemapOpacity(getPath(paths.basemapOpacity)));
  }

  if (hasPath(paths.showDataTableControl)) {
    dispatch(actions.setShowDataTableControl(getPath(paths.showDataTableControl)));
  }

  if (hasPath(paths.showSummaryTable)) {
    dispatch(actions.setShowSummaryTable(getPath(paths.showSummaryTable)));
  }

  if (hasPath(paths.showAnnotationsInLegend)) {
    dispatch(actions.setShowAnnotationsInLegend(getPath(paths.showAnnotationsInLegend)));
  }

  if (hasPath(paths.mapPitchAndBearing)) {
    dispatch(actions.setPitchAndBearing(getPath(paths.mapPitchAndBearing)));
  }

  if (hasPath(paths.mapZoomLevel)) {
    dispatch(actions.setMapZoomLevel(getPath(paths.mapZoomLevel)));
  }

  if (hasPath(paths.rowStripeStyle)) {
    dispatch(actions.setTableRowStripeStyleAction(getPath(paths.rowStripeStyle)));
  }

  dispatch(
    actions.setDataSource(
      getPath(formatString(paths.domain, primarySeriesIndex)),
      getPath(formatString(paths.datasetUid, primarySeriesIndex)),
      null,
      true,
      primarySeriesIndex,
    ),
  );
};
